/**
 * Feature E02-F04-T08 — "Add Information" block FRONT-END model (pure, DOM-free).
 *
 * 1:1 port of the legacy SPA `../EER app/public/assets/js/project.js` Stage-1
 * right column (~899-951 render, ~1166-1273 behaviour):
 *   - `manual-context-text` textarea → `data.manual_context.text`;
 *   - staged (pending) file picker → uploaded on "Save Information";
 *   - saved-links list with add (`showAddLinkDialog`/`addProjectLink`,
 *     validated `/^https?:\/\//i`) and delete (`deleteProjectLink`);
 *   - saved-files list with delete (`deleteProjectFile`).
 *
 * Thin shell — the non-render rules live here so `AddInformationPanel.tsx` stays a
 * thin shell. Reads `data.manual_context` defensively (the `data` bag is
 * `Record<string, unknown>`) through the shared `manualContextSchema` so a
 * drifted/garbled stored shape degrades to empty lists instead of throwing.
 */
import { manualContextSchema, type ProjectFile, type ProjectLink } from '@eer/shared';

/** Legacy `showAddLinkDialog` validation: must start with http:// or https://. */
export const VALID_LINK_URL_RE = /^https?:\/\//i;

/** True when `url` (trimmed) starts with http:// or https:// (case-insensitive). */
export function isValidLinkUrl(url: string): boolean {
  return VALID_LINK_URL_RE.test(url.trim());
}

/** The manual-context sub-shapes the Add Information panel reads/edits. */
export interface ManualContextView {
  readonly text: string;
  readonly links: readonly ProjectLink[];
  readonly files: readonly ProjectFile[];
}

const EMPTY_VIEW: ManualContextView = { text: '', links: [], files: [] };

/**
 * Re-hydrate `data.manual_context` (text + links + files) for the Add
 * Information panel. Defensive: an absent/garbled `manual_context` degrades to
 * `{ text:'', links:[], files:[] }` rather than throwing (parity with
 * `prekickoffModel.ts` → `readManualContext`).
 */
export function readManualContextView(
  data: Record<string, unknown> | null | undefined,
): ManualContextView {
  if (!data || typeof data !== 'object') return EMPTY_VIEW;
  const parsed = manualContextSchema.safeParse(
    (data as { manual_context?: unknown }).manual_context,
  );
  if (!parsed.success) return EMPTY_VIEW;
  const mc = parsed.data;
  return {
    text: mc.text ?? '',
    links: mc.links ?? [],
    files: (mc as { files?: ProjectFile[] }).files ?? [],
  };
}

/** Round bytes to whole KB for the pending-file size label (legacy parity). */
export function formatFileSizeKb(bytes: number): number {
  return Math.round(bytes / 1024);
}

/** Best-effort hostname (minus `www.`) for a link, `''` on an unparsable URL. */
export function linkHostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

/** The label shown for a saved link: title, else hostname, else the raw URL. */
export function linkDisplayLabel(link: ProjectLink): string {
  const title = (link.title ?? '').trim();
  if (title) return title;
  const host = linkHostname(link.url);
  return host || link.url;
}

/**
 * PK-QA-5 — the warning shown when a link's last scrape did NOT yield usable
 * content. `fetch_status` is `'failed'` (throw / non-2xx) or `'empty'` (2xx but
 * no extractable text); either means the link contributes nothing to the AI
 * analysis, so the panel shows this warning + a "Retry" (re-scrape) action.
 * Returns `null` for `'ok'` OR an absent status (legacy rows scraped before the
 * field existed are treated as fine — no false alarm).
 */
export function linkScrapeWarning(link: ProjectLink): string | null {
  switch (link.fetch_status) {
    case 'failed':
      return 'Could not load the link content';
    case 'empty':
      return 'No readable content found at this link';
    default:
      return null;
  }
}
